import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { SoccerKit } from '../../ui/SoccerKit/SoccerKit';
import { TeamCrest } from '../../competitions/TeamCrest/TeamCrest';
import { getSportDefinitionOrDefault, OUTCOME_SHAPES } from '../../../src/sports/sports.constants';
import { RaceCardIdentity } from './RaceCardIdentity';
import { InlineMarketPicker } from './InlineMarketPicker';
import { useMatchDetails } from '../../../src/create/hooks/useMatchDetails';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { getGameInlineCardStyles } from './GameInlineCard.styles';
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { useGuestWalkthroughStore } from '../../../src/shared/stores/guestWalkthroughStore';
import { WALKTHROUGH_STEPS } from '../../../src/create/constants/guestWalkthrough.constants';
import type { Game, PickSlip } from '../../../src/create/types/create.types';
import type { RefObject } from 'react';

const styles = getGameInlineCardStyles();

const SIDE_LABEL: Record<string, string> = { home: 'HOME', draw: 'DRAW', away: 'AWAY' };
const SIDE_A11Y_LABEL: Record<string, string> = { home: 'Home odds', draw: 'Draw odds', away: 'Away odds' };

// Mounts only when card is expanded — triggers useMatchDetails lazily
function ExpandedMarkets({ game, isWalkthroughCard = false }: { game: Game; isWalkthroughCard?: boolean }) {
  const { match, loading } = useMatchDetails(game.id);
  const { handleAddOtherPick, isOtherPickSelected } = useCreatePage();

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color="#A78BFA" />
      </View>
    );
  }

  const markets = match?.odds?.markets ?? [];
  if (markets.length === 0) {
    return (
      <View style={styles.noMarketsContainer}>
        <Text style={styles.noMarketsText}>No markets available</Text>
      </View>
    );
  }

  return (
    <InlineMarketPicker
      gameId={game.id}
      homeName={game.homeTeam}
      awayName={game.awayTeam}
      markets={markets}
      isSelected={isOtherPickSelected}
      onPick={handleAddOtherPick}
      isWalkthroughCard={isWalkthroughCard}
      markPickedChips
    />
  );
}

export interface GameInlineCardProps {
  game: Game;
  getPickSlipForGame: (gameId: string) => PickSlip | undefined;
  handlePickSelect: (gameId: string, pick: 'home' | 'draw' | 'away' | null) => void;
  isDisabled: boolean;
  picksCount: number;
  cardRef?: RefObject<View | null>;
  isWalkthroughGamesStep?: boolean;
  /** True for the spotlighted walkthrough card — forces expansion once the tour moves past games_list. */
  isWalkthroughCard?: boolean;
  onWalkthroughGameTap?: () => void;
}

/** Walkthrough steps after the card has been "opened" — the spotlighted card stays expanded through them. */
const EXPANDED_WALKTHROUGH_STEPS: ReadonlySet<string> = new Set([
  WALKTHROUGH_STEPS.MARKET_TAB,
  WALKTHROUGH_STEPS.MATCH_DETAILS,
  WALKTHROUGH_STEPS.MATCH_DETAILS_NEXT,
]);

export const GameInlineCard: React.FC<GameInlineCardProps> = ({
  game,
  getPickSlipForGame,
  handlePickSelect,
  isDisabled,
  picksCount,
  cardRef,
  isWalkthroughGamesStep = false,
  isWalkthroughCard = false,
  onWalkthroughGameTap,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const walkthroughStep = useGuestWalkthroughStore((s) => s.currentStep);

  // The spotlighted card auto-expands (reactively) once the tour advances past games_list,
  // so the market-tab / home-pick targets can mount and be measured.
  const expanded =
    isExpanded || (isWalkthroughCard && walkthroughStep !== null && EXPANDED_WALKTHROUGH_STEPS.has(walkthroughStep));

  const slip = getPickSlipForGame(game.id);
  const hasPick = picksCount > 0;

  const homeKit = useMemo(
    () => game.homeKit ?? getTeamKitConfig(game.homeTeam),
    [game.homeKit, game.homeTeam]
  );
  const awayKit = useMemo(
    () => game.awayKit ?? getTeamKitConfig(game.awayTeam),
    [game.awayKit, game.awayTeam]
  );

  // Football keeps its kit graphics untouched; a sport with no kit vocabulary gets a crest.
  // `sport` is absent on older cached payloads, which resolve to football (specs/multi-sport-challenges).
  const sportDefinition = useMemo(() => getSportDefinitionOrDefault(game.sport), [game.sport]);
  const usesKits = sportDefinition.identityMode === 'KIT';
  // A FIELD fixture has no two sides: no crest pairing in the header, and no home/draw/away row
  // to quick-pick from. Its picks are made from the driver list behind the markets below
  // (specs/formula-one-challenges AC-1.3, AC-1.7). Branch on the SHAPE, never the sport key.
  const isFieldSport = sportDefinition.shape === OUTCOME_SHAPES.FIELD;

  const toggleExpand = useCallback(() => {
    if (isWalkthroughGamesStep) {
      onWalkthroughGameTap?.();
      return;
    }
    setIsExpanded(prev => !prev);
  }, [isWalkthroughGamesStep, onWalkthroughGameTap]);

  const handleOddsPress = useCallback((side: 'home' | 'draw' | 'away') => {
    if (isDisabled || isWalkthroughGamesStep) return;
    handlePickSelect(game.id, slip?.prediction === side ? null : side);
  }, [isDisabled, isWalkthroughGamesStep, handlePickSelect, game.id, slip?.prediction]);

  return (
    <View ref={cardRef} style={[styles.card, hasPick && styles.cardPicked]} collapsable={false}>
      {/* Header row */}
      <SoundPressable
        tapSoundId={expanded ? SOUND_IDS.UI_SECTION_COLLAPSE : SOUND_IDS.UI_SECTION_EXPAND}
        style={styles.headerRow}
        onPress={toggleExpand}
        accessibilityRole="button"
        accessibilityLabel={isFieldSport ? (game.eventName ?? game.homeTeam) : `${game.homeTeam} vs ${game.awayTeam}`}
        accessibilityState={{ expanded }}
      >
        {isFieldSport ? <RaceCardIdentity game={game} /> : (
        <View style={styles.teamsColumn}>
          <View style={styles.teamRow}>
            {usesKits
              ? <SoccerKit primary={homeKit.primary} secondary={homeKit.secondary} type={homeKit.type} size={18} />
              : <TeamCrest teamName={game.homeTeam} sport={game.sport} size={18} />}
            <Text style={styles.teamName} numberOfLines={1}>{game.homeTeam}</Text>
          </View>
          <View style={styles.teamRow}>
            {usesKits
              ? <SoccerKit primary={awayKit.primary} secondary={awayKit.secondary} type={awayKit.type} size={18} />
              : <TeamCrest teamName={game.awayTeam} sport={game.sport} size={18} />}
            <Text style={styles.teamName} numberOfLines={1}>{game.awayTeam}</Text>
          </View>
        </View>
        )}

        <View style={styles.headerRight}>
          {/* Sport marker (specs/multi-sport-challenges AC-1.2): basketball and football sit in
              the same list, so a card must be identifiable without reading the team names.
              Football is the implicit default and stays unmarked, keeping its card unchanged. */}
          {/* The race card already carries the sport icon in its identity block, so a second
              marker here would be redundant. */}
          {!usesKits && !isFieldSport && (
            <View
              style={styles.sportBadge}
              accessibilityLabel={sportDefinition.label}
            >
              <Text style={styles.sportBadgeText}>{sportDefinition.label.slice(0, 3).toUpperCase()}</Text>
            </View>
          )}
          {game.oddsAreEstimated && (
            <View
              style={styles.estimatedBadge}
              accessibilityLabel="Estimated odds — not from a bookmaker"
            >
              <Text style={styles.estimatedBadgeText}>Est.</Text>
            </View>
          )}
          <Text style={styles.gameTime}>{game.time}</Text>
          {hasPick && (
            <View style={styles.betCountDot}>
              <Text style={styles.betCountDotText}>{picksCount}</Text>
            </View>
          )}
          {expanded
            ? <ChevronUp size={16} color="#A78BFA" />
            : <ChevronDown size={16} color="#A78BFA" />
          }
        </View>
      </SoundPressable>

      {/* 1X2 quick-pick row — always visible when collapsed, and never for a FIELD sport */}
      {!expanded && !isFieldSport && (
        <View style={styles.oddsRow}>
          {(['home', 'draw', 'away'] as const).map((side) => {
            const odd = game.odds[side];
            if (odd == null) return null;
            const isSelected = slip?.prediction === side;
            return (
              <Pressable
                key={side}
                style={({ pressed }) => [
                  styles.oddsTile,
                  isSelected && styles.oddsTileSelected,
                  isDisabled && !isSelected && styles.oddsTileDisabled,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.oddsTilePressed,
                ]}
                onPress={() => handleOddsPress(side)}
                disabled={isDisabled && !isSelected}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={`${SIDE_A11Y_LABEL[side]} ${odd.toFixed(2)}`}
              >
                <Text style={[styles.oddsSideLabel, isSelected && styles.oddsSideLabelSelected]}>
                  {SIDE_LABEL[side]}
                </Text>
                <Text style={[styles.oddsValue, isSelected && styles.oddsValueSelected]}>
                  {odd.toFixed(2)}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}

      {/* Expanded market picker */}
      {expanded && (
        <View style={styles.expandedPanel}>
          <ExpandedMarkets game={game} isWalkthroughCard={isWalkthroughCard} />
        </View>
      )}
    </View>
  );
};
